import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  Fade,
  Slide,
  useMediaQuery,
  useTheme,
  Stack,
  Fab,
  Grid,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Book as BookIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  FilterList as FilterIcon,
  PlaylistAdd as BulkAddIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { FacultySubjectService } from '../../services/facultySubjectService';
import { UserService } from '../../services/userService';
import { SubjectService } from '../../services/subjectService';
import { ClassService } from '../../services/classService';
import { 
  FacultySubjectAssignment, 
  FacultySubjectAssignmentFormData,
  BulkFacultyAssignmentFormData,
  User,
  Subject,
  Class 
} from '../../types';

interface AssignmentStats {
  totalAssignments: number;
  activeFaculty: number;
  totalSubjects: number;
  totalClasses: number;
}

export function FacultySubjectAssignmentEnhanced() {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [assignments, setAssignments] = useState<FacultySubjectAssignment[]>([]);
  const [faculty, setFaculty] = useState<User[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFaculty, setSelectedFaculty] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<FacultySubjectAssignment | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<FacultySubjectAssignment | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState<AssignmentStats>({ totalAssignments: 0, activeFaculty: 0, totalSubjects: 0, totalClasses: 0 });
  const [mounted, setMounted] = useState(false);

  const [formData, setFormData] = useState<FacultySubjectAssignmentFormData>({
    facultyId: '',
    subjectId: '',
    classIds: [],
    academicYear: new Date().getFullYear().toString(),
    semester: 1,
  });

  const [bulkFormData, setBulkFormData] = useState<BulkFacultyAssignmentFormData>({
    facultyId: '',
    assignments: [],
    academicYear: new Date().getFullYear().toString(),
    semester: 1,
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setMounted(true);
    loadData();
  }, [searchTerm, selectedFaculty, selectedSubject]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentsResult, facultyResult, subjectsResult, classesResult] = await Promise.all([
        FacultySubjectService.getAssignments(100),
        UserService.getUsersByRole('faculty'),
        SubjectService.getSubjects(100),
        ClassService.getClasses(100)
      ]);

      let filteredAssignments = assignmentsResult.assignments;
      
      if (searchTerm.trim()) {
        const searchResults = await FacultySubjectService.searchAssignments(searchTerm);
        filteredAssignments = searchResults;
      }
      
      if (selectedFaculty !== 'all') {
        filteredAssignments = filteredAssignments.filter(assignment => assignment.facultyId === selectedFaculty);
      }
      
      if (selectedSubject !== 'all') {
        filteredAssignments = filteredAssignments.filter(assignment => assignment.subjectId === selectedSubject);
      }

      setAssignments(filteredAssignments);
      setFaculty(facultyResult);
      setSubjects(subjectsResult.data);
      setClasses(classesResult.classes);
      calculateStats(assignmentsResult.assignments, facultyResult, subjectsResult.data, classesResult.classes);
    } catch (error) {
      showSnackbar('Failed to load data', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (allAssignments: FacultySubjectAssignment[], allFaculty: User[], allSubjects: Subject[], allClasses: Class[]) => {
    const activeFaculty = new Set(allAssignments.filter(a => a.isActive).map(a => a.facultyId)).size;
    const totalSubjects = new Set(allAssignments.map(a => a.subjectId)).size;
    const totalClasses = new Set(allAssignments.flatMap(a => a.classIds)).size;
    
    setStats({
      totalAssignments: allAssignments.length,
      activeFaculty,
      totalSubjects,
      totalClasses
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.facultyId) {
      errors.facultyId = 'Faculty is required';
    }

    if (!formData.subjectId) {
      errors.subjectId = 'Subject is required';
    }

    if (formData.classIds.length === 0) {
      errors.classIds = 'At least one class is required';
    }

    if (!formData.academicYear) {
      errors.academicYear = 'Academic year is required';
    }

    if (formData.semester < 1 || formData.semester > 8) {
      errors.semester = 'Semester must be between 1 and 8';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAssignment = () => {
    setEditingAssignment(null);
    setFormData({
      facultyId: '',
      subjectId: '',
      classIds: [],
      academicYear: new Date().getFullYear().toString(),
      semester: 1,
    });
    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleBulkAssignment = () => {
    setBulkFormData({
      facultyId: '',
      assignments: [],
      academicYear: new Date().getFullYear().toString(),
      semester: 1,
    });
    setOpenBulkDialog(true);
  };

  const handleEditAssignment = (assignment: FacultySubjectAssignment) => {
    setEditingAssignment(assignment);
    setFormData({
      facultyId: assignment.facultyId,
      subjectId: assignment.subjectId,
      classIds: assignment.classIds,
      academicYear: assignment.academicYear,
      semester: assignment.semester,
    });
    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleDeleteAssignment = (assignment: FacultySubjectAssignment) => {
    setAssignmentToDelete(assignment);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingAssignment) {
        await FacultySubjectService.updateAssignment(editingAssignment.id, formData);
        showSnackbar('Assignment updated successfully', 'success');
      } else {
        await FacultySubjectService.createAssignment(formData);
        showSnackbar('Assignment created successfully', 'success');
      }
      setOpenDialog(false);
      loadData();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to save assignment', 'error');
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkFormData.facultyId || bulkFormData.assignments.length === 0) {
      showSnackbar('Please select faculty and add assignments', 'error');
      return;
    }

    try {
      await FacultySubjectService.createBulkAssignments(bulkFormData);
      showSnackbar('Bulk assignments created successfully', 'success');
      setOpenBulkDialog(false);
      loadData();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to create bulk assignments', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!assignmentToDelete) return;

    try {
      await FacultySubjectService.deleteAssignment(assignmentToDelete.id);
      showSnackbar('Assignment deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);
      loadData();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to delete assignment', 'error');
    }
  };

  const toggleAssignmentStatus = async (assignment: FacultySubjectAssignment) => {
    try {
      await FacultySubjectService.toggleAssignmentStatus(assignment.id);
      showSnackbar(`Assignment ${assignment.isActive ? 'deactivated' : 'activated'} successfully`, 'success');
      loadData();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to update assignment status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const addToBulkAssignment = () => {
    setBulkFormData(prev => ({
      ...prev,
      assignments: [...prev.assignments, { subjectId: '', classIds: [] }]
    }));
  };

  const updateBulkAssignment = (index: number, field: 'subjectId' | 'classIds', value: any) => {
    setBulkFormData(prev => ({
      ...prev,
      assignments: prev.assignments.map((assignment, i) => 
        i === index ? { ...assignment, [field]: value } : assignment
      )
    }));
  };

  const removeBulkAssignment = (index: number) => {
    setBulkFormData(prev => ({
      ...prev,
      assignments: prev.assignments.filter((_, i) => i !== index)
    }));
  };

  // Mobile card view for assignments
  const AssignmentCard = ({ assignment }: { assignment: FacultySubjectAssignment }) => (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
              {assignment.facultyName}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {assignment.subjectCode} - {assignment.subjectName}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Classes: {assignment.classNames?.join(', ')}
            </Typography>
          </Box>
          <Chip
            label={assignment.isActive ? 'Active' : 'Inactive'}
            color={assignment.isActive ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${assignment.academicYear}`}
            size="small"
            color="info"
            variant="outlined"
          />
          <Chip
            label={`Semester ${assignment.semester}`}
            size="small"
            variant="outlined"
          />
          <Badge badgeContent={assignment.classIds.length} color="primary">
            <Chip
              label="Classes"
              size="small"
              variant="outlined"
            />
          </Badge>
        </Stack>
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit Assignment">
            <IconButton
              size="small"
              onClick={() => handleEditAssignment(assignment)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={assignment.isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => toggleAssignmentStatus(assignment)}
              sx={{ color: assignment.isActive ? 'warning.main' : 'success.main' }}
            >
              {assignment.isActive ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Assignment">
            <IconButton
              size="small"
              onClick={() => handleDeleteAssignment(assignment)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !mounted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in={mounted} timeout={800}>
      <Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
            Faculty Subject Assignments
          </Typography>
          {!isMobile && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<BulkAddIcon />}
              onClick={handleBulkAssignment}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Bulk Assign
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateAssignment}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 8px 24px -8px rgba(25, 118, 210, 0.4)',
              }}
            >
              New Assignment
            </Button>
          </Stack>
          )}
        </Box>

        {/* Stats Cards */}
        <Slide direction="up" in={mounted} timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={2}
              sx={{ 
                justifyContent: "space-between",
                flexWrap: isMobile ? "nowrap" : "wrap"
              }}
            >
              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssignmentIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography color="textSecondary" variant="body2">
                        Total Assignments
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {stats.totalAssignments}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>
            
              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography color="textSecondary" variant="body2">
                        Active Faculty
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {stats.activeFaculty}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BookIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography color="textSecondary" variant="body2">
                        Assigned Subjects
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {stats.totalSubjects}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography color="textSecondary" variant="body2">
                        Total Classes
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {stats.totalClasses}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>
            </Stack>
          </Box>
        </Slide>

        {/* Filters */}
        <Slide direction="up" in={mounted} timeout={1200}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
            <Stack spacing={2}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  size={isMobile ? "medium" : "small"}
                  placeholder="Search assignments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ borderRadius: 2 }}
                />
              </Box>
              
              <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2}
                sx={{ alignItems: isMobile ? "stretch" : "center" }}
              >
                <Box sx={{ flex: isMobile ? "none" : "1" }}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Faculty</InputLabel>
                    <Select
                      value={selectedFaculty}
                      onChange={(e) => setSelectedFaculty(e.target.value)}
                      label="Faculty"
                    >
                      <MenuItem value="all">All Faculty</MenuItem>
                      {faculty.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.firstName && f.lastName ? `${f.firstName} ${f.lastName}` : f.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ flex: isMobile ? "none" : "1" }}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Subject</InputLabel>
                    <Select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      label="Subject"
                    >
                      <MenuItem value="all">All Subjects</MenuItem>
                      {subjects.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.code} - {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ flex: isMobile ? "none" : "0 0 auto" }}>
                  <Button
                    fullWidth={isMobile}
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Slide>

        {/* Assignments Display */}
        <Slide direction="up" in={mounted} timeout={1400}>
          <Paper sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)', overflow: 'hidden' }}>
            {isMobile ? (
              // Mobile Card View
              <Box sx={{ p: 2 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : assignments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No assignments found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {assignments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((assignment) => (
                        <AssignmentCard key={assignment.id} assignment={assignment} />
                      ))}
                  </>
                )}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={assignments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            ) : (
              // Desktop Table View
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Faculty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Classes</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Academic Year</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Semester</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : assignments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="textSecondary">
                              No assignments found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        assignments
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((assignment, index) => (
                            <TableRow 
                              key={assignment.id}
                              sx={{ 
                                '&:hover': { bgcolor: 'grey.50' },
                                animation: `fadeIn 0.6s ease-in-out ${index * 0.1}s both`,
                                '@keyframes fadeIn': {
                                  from: { opacity: 0, transform: 'translateY(20px)' },
                                  to: { opacity: 1, transform: 'translateY(0)' },
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {assignment.facultyName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {assignment.subjectCode}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {assignment.subjectName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Badge badgeContent={assignment.classIds.length} color="primary">
                                  <Box>
                                    <Typography variant="body2">
                                      {assignment.classNames?.slice(0, 2).join(', ')}
                                      {assignment.classNames && assignment.classNames.length > 2 && '...'}
                                    </Typography>
                                  </Box>
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {assignment.academicYear}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`Sem ${assignment.semester}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={assignment.isActive ? 'Active' : 'Inactive'}
                                  color={assignment.isActive ? 'success' : 'default'}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Edit Assignment">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditAssignment(assignment)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={assignment.isActive ? 'Deactivate' : 'Activate'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleAssignmentStatus(assignment)}
                                      sx={{ color: assignment.isActive ? 'warning.main' : 'success.main' }}
                                    >
                                      {assignment.isActive ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Assignment">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteAssignment(assignment)}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={assignments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>
        </Slide>

        {/* Floating Action Buttons for Mobile */}
        {isMobile && (
          <>
            <Fab
              color="primary"
              aria-label="new assignment"
              onClick={handleCreateAssignment}
              sx={{
                position: 'fixed',
                bottom: 88,
                right: 24,
                zIndex: 1000,
              }}
            >
              <AddIcon />
            </Fab>
            <Fab
              color="secondary"
              aria-label="bulk assign"
              onClick={handleBulkAssignment}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
              }}
            >
              <BulkAddIcon />
            </Fab>
          </>
        )}

        {/* Assignment Form Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: { 
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingAssignment ? 'Edit Faculty Assignment' : 'Create Faculty Assignment'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth error={!!validationErrors.facultyId}>
                    <InputLabel>Faculty</InputLabel>
                    <Select
                      value={formData.facultyId}
                      onChange={(e) => setFormData({ ...formData, facultyId: e.target.value })}
                      label="Faculty"
                    >
                      {faculty.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.firstName && f.lastName ? `${f.firstName} ${f.lastName}` : f.username}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.facultyId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {validationErrors.facultyId}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth error={!!validationErrors.subjectId}>
                    <InputLabel>Subject</InputLabel>
                    <Select
                      value={formData.subjectId}
                      onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}
                      label="Subject"
                    >
                      {subjects.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.code} - {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {validationErrors.subjectId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {validationErrors.subjectId}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              </Stack>
              
              <Box>
                <Autocomplete
                  multiple
                  options={classes}
                  getOptionLabel={(option) => option.name}
                  value={classes.filter(c => formData.classIds.includes(c.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, classIds: newValue.map(c => c.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Classes"
                      error={!!validationErrors.classIds}
                      helperText={validationErrors.classIds}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                />
              </Box>
              
              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Academic Year"
                    value={formData.academicYear}
                    onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
                    error={!!validationErrors.academicYear}
                    helperText={validationErrors.academicYear}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Semester"
                    type="number"
                    value={formData.semester}
                    onChange={(e) => setFormData({ ...formData, semester: parseInt(e.target.value) || 1 })}
                    error={!!validationErrors.semester}
                    helperText={validationErrors.semester}
                    inputProps={{ min: 1, max: 8 }}
                  />
                </Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setOpenDialog(false)} 
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {editingAssignment ? 'Update Assignment' : 'Create Assignment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Assignment Dialog */}
        <Dialog 
          open={openBulkDialog} 
          onClose={() => setOpenBulkDialog(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: { 
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bulk Faculty Assignment
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Faculty</InputLabel>
                  <Select
                    value={bulkFormData.facultyId}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, facultyId: e.target.value })}
                    label="Faculty"
                  >
                    {faculty.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.firstName && f.lastName ? `${f.firstName} ${f.lastName}` : f.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Academic Year"
                    value={bulkFormData.academicYear}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, academicYear: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Semester"
                    type="number"
                    value={bulkFormData.semester}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, semester: parseInt(e.target.value) || 1 })}
                    inputProps={{ min: 1, max: 8 }}
                  />
                </Box>
              </Stack>

              <Divider />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Subject Assignments</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addToBulkAssignment}
                    startIcon={<AddIcon />}
                  >
                    Add Assignment
                  </Button>
                </Box>

                {bulkFormData.assignments.map((assignment, index) => (
                  <Card key={index} sx={{ mb: 2, p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">Assignment {index + 1}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeBulkAssignment(index)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Subject</InputLabel>
                            <Select
                              value={assignment.subjectId}
                              onChange={(e) => updateBulkAssignment(index, 'subjectId', e.target.value)}
                              label="Subject"
                            >
                              {subjects.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                  {s.code} - {s.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: 2 }}>
                          <Autocomplete
                            multiple
                            size="small"
                            options={classes}
                            getOptionLabel={(option) => option.name}
                            value={classes.filter(c => assignment.classIds.includes(c.id))}
                            onChange={(_, newValue) => {
                              updateBulkAssignment(index, 'classIds', newValue.map(c => c.id));
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Classes" />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  label={option.name}
                                  {...getTagProps({ index })}
                                  size="small"
                                />
                              ))
                            }
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </Card>
                ))}

                {bulkFormData.assignments.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No assignments added yet. Click "Add Assignment" to start.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setOpenBulkDialog(false)} 
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkSubmit} 
              variant="contained"
              disabled={!bulkFormData.facultyId || bulkFormData.assignments.length === 0}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Create Bulk Assignments
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: { 
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the assignment for "{assignmentToDelete?.facultyName}" 
              teaching "{assignmentToDelete?.subjectName}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete} 
              color="error" 
              variant="contained"
              sx={{ 
                textTransform: 'none', 
                borderRadius: 2,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Delete Assignment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
} 